import React, { useState, useEffect } from 'react';
import { BookOpen, Headphones, Compass, Wrench, Archive, Feather, ScrollText, ArrowLeft, Sparkles, Calendar, Heart, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import GardenSVG from '../../components/GardenSVG';
import AltarDelPresente from '../../components/AltarDelPresente';

/* Golden cursor for interactive zones */
const GOLDEN_CURSOR = `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32'><circle cx='16' cy='16' r='3.5' fill='%23D4AF37'/><circle cx='16' cy='16' r='9' fill='%23D4AF37' opacity='.18'/><circle cx='16' cy='16' r='14' fill='%23D4AF37' opacity='.06'/></svg>") 16 16, pointer`;

const SEASON_LABELS = ['Invierno', 'Despertar', 'Primavera', 'Verano', 'Cosecha', 'Recogimiento'];
const TIME_LABELS = { madrugada: 'Madrugada', manana: 'Mañana', tarde: 'Tarde', noche: 'Noche' };
const MOON_LABELS = {
  nueva: 'Luna Nueva', creciente_inicial: 'Luna Creciente', cuarto_creciente: 'Cuarto Creciente',
  creciente_gibosa: 'Creciente Gibosa', llena: 'Luna Llena', menguante_gibosa: 'Menguante Gibosa',
  cuarto_menguante: 'Cuarto Menguante', menguante_final: 'Menguante Final',
};
const CONTENT_TYPE_CONFIG = {
  lectura: { icon: BookOpen, label: 'Lectura Consciente', color: '#c9a227' },
  ajuste: { icon: Compass, label: 'Ajuste Quincenal', color: '#7a9a5a' },
  meditacion: { icon: Headphones, label: 'Meditación', color: '#8a7aaa' },
  herramienta: { icon: Wrench, label: 'Herramienta Práctica', color: '#b88a5a' },
};

/* ═══ Boticario Drawer Component ═══ */
const BoticarioDrawer = ({ icon: Icon, label, sub, onClick, badge, dimmed, testId }) => (
  <button
    onClick={onClick}
    className={`group flex flex-col items-center gap-1.5 rounded-lg p-4 transition-all hover:brightness-[1.03] active:brightness-[0.97] ${dimmed ? 'opacity-50' : ''}`}
    style={{
      background: 'linear-gradient(180deg, #f5edd8 0%, #efe5cc 60%, #e8dcc2 100%)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5), 0 2px 4px rgba(139,115,64,0.1)',
      border: '1px solid rgba(201,162,39,0.12)',
    }}
    data-testid={testId}
  >
    <div className="w-10 h-1.5 rounded-full mb-0.5 transition-colors"
      style={{
        background: 'linear-gradient(180deg, #d4b85c 0%, #c9a227 50%, #b8922a 100%)',
        boxShadow: '0 1px 2px rgba(139,115,64,0.3)',
      }} />
    <Icon className="w-4 h-4 text-[#8b7340]/55 group-hover:text-[#8b7340]/75 transition-colors" />
    <span className="text-[#5a4a2a]/80 text-xs font-medium group-hover:text-[#5a4a2a] transition-colors">{label}</span>
    {sub && <span className="text-[#8b7340]/55 text-[10px]">{sub}</span>}
    {badge && <span className="text-[#8b7340]/50 text-[10px]">{badge}</span>}
  </button>
);

const BrassSeparator = () => (
  <div className="h-px mx-2 my-1" style={{ background: 'linear-gradient(to right, transparent, rgba(201,162,39,0.15), transparent)' }} />
);

const CampusJardin = ({ onNavigate }) => {
  const navigate = useNavigate();
  const [garden, setGarden] = useState(null);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredZone, setHoveredZone] = useState(null);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => { fetchData(); }, []);

  /* First-visit secret hint — only shows once */
  useEffect(() => {
    const key = 'campus_garden_explored';
    if (!localStorage.getItem(key)) {
      const timer = setTimeout(() => setShowHint(true), 2500);
      const hide = setTimeout(() => {
        setShowHint(false);
        localStorage.setItem(key, '1');
      }, 8500);
      return () => { clearTimeout(timer); clearTimeout(hide); };
    }
  }, []);

  const fetchData = async () => {
    try {
      const [gardenRes, contentRes] = await Promise.all([
        api.get('/campus/garden'),
        api.get('/campus/content/current'),
      ]);
      setGarden(gardenRes.data);
      setContent(contentRes.data.contents || []);
    } catch (err) {
      console.error('Error loading campus:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf8f4] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const season = garden?.season || { index: 0, name: 'invierno' };
  const moon = garden?.moon || {};
  const portal = garden?.portal || {};
  const personal = garden?.personal || {};
  const timeOfDay = garden?.time_of_day || 'tarde';

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 7) return 'En la quietud de la madrugada';
    if (hour < 14) return 'Buenos días';
    if (hour < 20) return 'Buenas tardes';
    return 'Buenas noches';
  };

  return (
    <div className="min-h-screen bg-[#faf8f4]" data-testid="campus-jardin">
      {/* ═══ HERO: GARDEN WITH OVERLAID INFO ═══ */}
      <div className="relative" data-testid="garden-illustration">
        {/* Back button - floats over garden */}
        <div className="absolute top-4 left-4 z-20">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm bg-black/30 backdrop-blur-md rounded-full px-3 py-1.5"
            data-testid="campus-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
        </div>

        {/* Title pill — white frosted glass with golden text */}
        <div className="absolute top-5 left-0 right-0 z-10 flex justify-center pointer-events-none">
          <div className="bg-white/80 backdrop-blur-lg rounded-full px-7 py-2.5 border border-white/50"
            style={{ boxShadow: '0 4px 20px rgba(0,0,0,.08)' }}>
            <p className="text-[#8b6914] text-xs tracking-[0.3em] uppercase font-medium">Tu Jardín de Interioridad</p>
          </div>
        </div>

        {/* Greeting — gold text, secondary to title */}
        <div className="absolute top-[72px] left-0 right-0 z-10 text-center pointer-events-none">
          <h1 className="text-white text-2xl sm:text-3xl font-normal tracking-wide" style={{ textShadow: '0 0 4px rgba(0,0,0,.7), 0 0 4px rgba(0,0,0,.7), 0 0 10px rgba(0,0,0,.4), 0 0 20px rgba(0,0,0,.2)' }}>
            {getGreeting()}.
          </h1>

          {portal.active && (
            <div className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 bg-white/60 backdrop-blur-md border border-[#c9a227]/30 rounded-full pointer-events-auto">
              <div className="w-2 h-2 bg-[#c9a227] rounded-full animate-pulse" />
              <span className="text-[#8b6914] text-xs tracking-wider font-medium">Portal {portal.portal} activo</span>
            </div>
          )}
        </div>

        {/* The garden illustration */}
        <GardenSVG
          season={season.index}
          timeOfDay={timeOfDay}
          moon={moon}
          personal={personal}
          hoveredZone={hoveredZone}
        />

        {/* Interactive zones — clickable areas over garden features */}
        <div className="absolute inset-0 z-10">
          <style>{`
            @keyframes inscriptionReveal {
              from { opacity: 0; transform: translateY(6px); }
              to { opacity: 1; transform: translateY(0); }
            }
            .zone-label {
              opacity: 0;
              pointer-events: none;
              transition: opacity 0s;
            }
            .group:hover .zone-label {
              animation: inscriptionReveal .6s ease-out both;
            }
          `}</style>

          {/* Portal zone — leads to Templo del Corazón */}
          <button
            onClick={() => onNavigate('camara')}
            onMouseEnter={() => setHoveredZone('portal')}
            onMouseLeave={() => setHoveredZone(null)}
            className="absolute group"
            style={{ left: '2%', top: '8%', width: '18%', height: '55%', cursor: GOLDEN_CURSOR }}
            aria-label="Entrar al Templo del Corazón"
            data-testid="garden-portal-zone"
          >
            <div className="w-full h-full rounded-2xl border border-transparent group-hover:border-[#c9a227]/20 transition-all duration-700" />
            <div className="zone-label absolute bottom-[12%] left-1/2 -translate-x-1/2 whitespace-nowrap">
              <div className="px-5 py-3.5 rounded-xl border border-[#c9a227]/20"
                style={{ background: 'rgba(255,253,248,0.95)', backdropFilter: 'blur(14px)', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                <p className="text-[#3d2b0e] text-xs tracking-[0.15em] uppercase font-medium text-center mb-1.5"
                  style={{ textShadow: 'none' }}>Templo del Corazón</p>
                <p className="text-[#6b5a30] text-[11px] font-light text-center leading-relaxed"
                  style={{ textShadow: 'none' }}>Un espacio de escucha íntima donde<br/>el corazón se expresa sin filtros.</p>
              </div>
            </div>
          </button>

          {/* Tree roots zone — leads to Núcleo de Gaia */}
          <button
            onClick={() => onNavigate('nucleo')}
            onMouseEnter={() => setHoveredZone('tree')}
            onMouseLeave={() => setHoveredZone(null)}
            className="absolute group"
            style={{ right: '0%', top: '15%', width: '22%', height: '60%', cursor: GOLDEN_CURSOR }}
            aria-label="Entrar al Núcleo de Gaia"
            data-testid="garden-tree-zone"
          >
            <div className="w-full h-full rounded-2xl border border-transparent group-hover:border-[#c9a227]/20 transition-all duration-700" />
            <div className="zone-label absolute bottom-[5%] right-[10%] whitespace-nowrap">
              <div className="px-5 py-3.5 rounded-xl border border-[#c9a227]/20"
                style={{ background: 'rgba(255,253,248,0.95)', backdropFilter: 'blur(14px)', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                <p className="text-[#3d2b0e] text-xs tracking-[0.15em] uppercase font-medium text-center mb-1.5"
                  style={{ textShadow: 'none' }}>Núcleo de Gaia</p>
                <p className="text-[#6b5a30] text-[11px] font-light text-center leading-relaxed"
                  style={{ textShadow: 'none' }}>La raíz cristalina. Un lugar para conectar<br/>con la tierra y sus frecuencias.</p>
              </div>
            </div>
          </button>
        </div>

        {/* First-visit hint — appears once, then never again */}
        {showHint && (
          <div className="absolute inset-x-0 top-[22%] z-20 flex justify-center pointer-events-none" data-testid="garden-hint">
            <p className="text-white/70 text-sm font-light tracking-widest italic px-6 py-2.5 bg-black/30 backdrop-blur-md rounded-full"
              style={{ animation: 'inscriptionReveal 1.2s ease-out both', textShadow: '0 1px 8px rgba(0,0,0,.4)' }}>
              Tu jardín tiene lugares secretos. Explora con tu presencia.
            </p>
          </div>
        )}

        {/* Instruction hint floating over garden bottom */}
        <div className="absolute bottom-8 left-0 right-0 flex justify-center z-10 pointer-events-none">
          <div className="bg-white/80 backdrop-blur-lg rounded-full px-7 py-2.5 border border-white/50"
            style={{ boxShadow: '0 4px 20px rgba(0,0,0,.08)' }}>
            <span className="text-[#5a4a2a]/80 text-xs font-light italic tracking-wide">Navega sobre el jardín para descubrir sus secretos mágicos</span>
          </div>
        </div>
      </div>

      {/* ═══ ALTAR DEL PRESENTE — Bridge between garden and content ═══ */}
      <div className="py-[5vh]">
        <AltarDelPresente />
      </div>

      {/* ═══ CONTENT SECTION ═══ */}
      <div className="max-w-2xl mx-auto px-4 pb-24 pt-2">
        {/* Content of the month */}
        <div className="mb-8">
          <h2 className="text-[#5c4a2a]/50 text-xs tracking-[0.2em] uppercase mb-4">Contenido del mes</h2>
          <div className="space-y-3">
            {content.length === 0 ? (
              <p className="text-[#5c4a2a]/40 text-sm text-center py-8">
                El contenido de este mes se está preparando...
              </p>
            ) : (
              content.map((item) => {
                const config = CONTENT_TYPE_CONFIG[item.content_type] || CONTENT_TYPE_CONFIG.lectura;
                const Icon = config.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => onNavigate('content', { contentId: item.id })}
                    className="w-full group"
                    data-testid={`campus-content-${item.content_type}`}
                  >
                    <div className="flex items-center gap-4 bg-white hover:bg-white border border-[#5c4a2a]/[0.06] hover:border-[#c9a227]/20 rounded-xl px-5 py-4 transition-all hover:shadow-sm">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${config.color}15` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: config.color }} />
                      </div>
                      <div className="text-left flex-1 min-w-0">
                        <h3 className="text-[#5c4a2a]/80 text-sm group-hover:text-[#5c4a2a] transition-colors truncate">
                          {item.title}
                        </h3>
                        <p className="text-[#5c4a2a]/40 text-xs mt-0.5">{config.label}</p>
                      </div>
                      {!item.is_read && (
                        <div className="w-2 h-2 bg-[#c9a227] rounded-full flex-shrink-0 animate-pulse" />
                      )}
                      {item.is_read && (
                        <div className="w-2 h-2 bg-[#5c4a2a]/10 rounded-full flex-shrink-0" />
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Navigation — Apothecary Cabinet */}
        <div className="relative rounded-2xl overflow-hidden border border-[#c9a227]/15 shadow-sm"
          style={{ background: 'linear-gradient(180deg, #f0e6d0 0%, #ede0c8 100%)' }}
          data-testid="mueble-boticario">

          {/* Cabinet top — Inscripción grabada */}
          <div className="flex items-center justify-center gap-3 py-3.5 border-b border-[#c9a227]/15"
            style={{ background: 'linear-gradient(180deg, #ede0c8, #e8d8be)' }}>
            <div className="w-12 h-px bg-[#c9a227]/30" />
            <span className="text-[#6b5a30]/75 text-xs tracking-[0.25em] uppercase"
              style={{ textShadow: '0 1px 0 rgba(255,245,220,0.6), 0 -1px 0 rgba(100,80,30,0.08)' }}>
              Boticario
            </span>
            <div className="w-12 h-px bg-[#c9a227]/30" />
          </div>

          {/* Drawers — Layout B: 2×4 with brass separators */}
          <div className="p-4 space-y-1" data-testid="boticario-drawers">
            {/* Row 1 */}
            <div className="grid grid-cols-2 gap-3">
              <BoticarioDrawer key_id="bienvenida" icon={Sparkles} label="Bienvenida" sub="Instrucciones del espacio"
                onClick={() => onNavigate('bienvenida')} testId="campus-bienvenida-btn" />
              <BoticarioDrawer key_id="bitacora" icon={Feather} label="Mi Bitácora" sub="Portal · Decretos · Resonancias"
                onClick={() => onNavigate('bitacora')} badge={personal.bitacora_entries ? `${personal.bitacora_entries} entradas` : null} testId="campus-bitacora-btn" />
            </div>
            <BrassSeparator />
            {/* Row 2 */}
            <div className="grid grid-cols-2 gap-3">
              <BoticarioDrawer key_id="archivo" icon={Archive} label="Archivo" sub="Meses anteriores"
                onClick={() => onNavigate('archive')} testId="campus-archive-btn" />
              <BoticarioDrawer key_id="calendario" icon={Calendar} label="Calendario Energético" sub="Portales · Sabbats · Lunas"
                onClick={() => onNavigate('calendario')} testId="campus-calendario-btn" />
            </div>
            <BrassSeparator />
            {/* Row 3 */}
            <div className="grid grid-cols-2 gap-3">
              <BoticarioDrawer key_id="meditaciones" icon={Headphones} label="Meditaciones" sub="Activaciones mensuales"
                onClick={() => onNavigate('meditaciones')} testId="campus-meditaciones-btn" />
              <BoticarioDrawer key_id="herramientas" icon={Wrench} label="Herramientas" sub="Dinámicas del proceso"
                onClick={() => onNavigate('herramientas')} testId="campus-herramientas-btn" />
            </div>
            <BrassSeparator />
            {/* Row 4 */}
            <div className="grid grid-cols-2 gap-3">
              <BoticarioDrawer key_id="recomendar" icon={Heart} label="Recomendar" sub="Expande la red geométrica"
                onClick={() => onNavigate('recomendar')} testId="campus-recomendar-btn" />
              <BoticarioDrawer key_id="comunidad" icon={Users} label="Comunidad" sub="Germinando..."
                onClick={() => onNavigate('comunidad')} dimmed testId="campus-comunidad-btn" />
            </div>
          </div>

          {/* Cabinet bottom trim */}
          <div className="h-2 border-t border-[#c9a227]/10"
            style={{ background: 'linear-gradient(180deg, #e5d9c0, #ddd0b5)' }} />
        </div>
      </div>
    </div>
  );
};

export default CampusJardin;
